/**
 * @fileoverview Contains the generic interface for iterating over the dom path
 * an event has traveled. These generators are meant to be singletons so you
 * should not construct them yourself. You should use the static factory method
 * getGenerator instead.
 */

'use strict';

var Property = require('./syntax').Property;

/** @interface */
function Generator() {}

// jscs:disable jsDoc
/**
 * @return {Element} The next element in the generator or null if none found.
 */
Generator.prototype.next = function () {};
// jscs:enable jsDoc

/**
 * Constructs a generator of all the ancestors of an element.
 * @constructor
 * @implements {Generator}
 */
function Ancestors() {
  /**
   * @type {Element}
   * @private
   */
  this.node_ = null;

  /**
   * @type {Element}
   * @private
   */
  this.container_ = null;
}

/**
 * Resets an ancestors generator of an element with a new target and container.
 * @param {!Element} target the element to start walking ancestors at.
 * @param {!Element} container the element to stop walking ancestors at.
 * @return {!Generator}
 * @private
 */
Ancestors.prototype.reset_ = function (target, container) {
  this.node_ = target;
  this.container_ = container;
  return this;
};

/** @override */
Ancestors.prototype.next = function () {
  // Walk to the parent node, unless the node has a different owner in
  // which case we walk to the owner.
  var curr = this.node_;
  if (this.node_ && this.node_ != this.container_) {
    this.node_ = this.node_[Property.OWNER] || this.node_.parentNode;
  } else {
    this.node_ = null;
  }

  return curr;
};

/**
 * A reusable generator for dom ancestor walks.
 * @type {!Ancestors}
 * @private
 */
var ancestors_ = new Ancestors();

/**
 * Constructs a generator of all elements in a path array.
 * Correctly handles syntax.Property.OWNER on elements.
 * @constructor
 * @implements {Generator}
 */
function EventPath() {
  /**
   * @type {!Array.<!Element>}
   * @private
   */
  this.path_ = [];

  /**
   * @type {number}
   * @private
   */
  this.idx_ = 0;

  /**
   * @type {Element}
   * @private
   */
  this.container_ = null;

  /**
   * @type {boolean}
   * @private
   */
  this.usingAncestors_ = false;
}

/**
 * Resets an EventPath with a new path and container.
 * @param {!Array.<!Element>} path
 * @param {!Element} container
 * @return {!Generator}
 * @private
 */
EventPath.prototype.reset_ = function (path, container) {
  this.path_ = path;
  this.idx_ = 0;
  this.container_ = container;
  this.usingAncestors_ = false;
  return this;
};

/** @override */
EventPath.prototype.next = function () {
  // TODO(user): If we could ban OWNERS for all users of event.path
  // then you could greatly simplify the code here.
  if (this.usingAncestors_) {
    return ancestors_.next();
  }
  if (this.idx_ != this.path_.length) {
    var curr = this.path_[this.idx_];
    this.idx_++;
    if (curr != this.container_) {
      // NOTE(user): The presence of the OWNER property indicates that
      // the user wants to override the browsers expected event path with
      // one of their own. The eventpath generator still needs to respect
      // the OWNER property since this is used by a lot of jsactions
      // consumers.
      if (curr && curr[Property.OWNER]) {
        this.usingAncestors_ = true;
        ancestors_.reset_(
            curr[Property.OWNER],
            /** @type {!Element} */(this.container_));
      }
    }
    return curr;
  }
  return null;
};

/**
 * A reusable generator for dom ancestor walks.
 * @type {!EventPath}
 * @private
 */
var eventPath_ = new EventPath();

/**
 * Return the correct dom generator for a given event.
 * @param {!Event} e the event.
 * @param {!Element} target the events target element.
 * @param {!Element} container the jsaction container.
 * @return {!Generator}
 */
function getGenerator(e, target, container) {
  return e.path ? eventPath_.reset_(e.path, container) :
                  ancestors_.reset_(target, container);
}

module.exports = {
  getGenerator: getGenerator,
  Ancestors: Ancestors,
  EventPath: EventPath,
  Generator: Generator,
  ancestors_: ancestors_,
  eventPath_: eventPath_
};



/*****************
 ** WEBPACK FOOTER
 ** ./web_modules/jsaction/lib/generator.js
 ** module id = 197
 ** module chunks = 1
 **/